import { computed, makeObservable } from 'mobx';

import { TableViewMode, TableVisibilityLevel } from 'entities/Table';
import { ToggleStateStore } from 'stores/common';
import { TableFactoryStore } from 'stores/common/table';
import { ILocalStore } from 'stores/interfaces';
import { debounce } from 'utils/debounce';

import { BasicFormStore } from '../BasicFormStore';

import { PublishModal } from './PublishModal';
import { IVerificationFormStore } from './interface';

type VerificationFormStoreParams = {
  basicStore: BasicFormStore;
};

const DEBOUNCE_REQUEST_TIME = 700;

class VerificationFormStore implements ILocalStore, IVerificationFormStore {
  constructor({ basicStore }: VerificationFormStoreParams) {
    this._basicStore = basicStore;

    makeObservable<VerificationFormStore>(this, {
      booleanModelToVisibility: computed,
      publishingTable: computed,
      fetchingTable: computed,
    });
  }

  readonly tableStore: TableFactoryStore = new TableFactoryStore({
    showNotVisibleByDefaultColumns: true,
    virtualized: true,
  });

  private readonly _basicStore: BasicFormStore;

  /** Дебаунсим switch, чтобы клиент на спамил наш сервер запросами */
  private readonly _debouncedFetch = debounce(async () => {
    this.tableStore.cancelInit();
    await this.init();
  }, DEBOUNCE_REQUEST_TIME);

  readonly visibilityToggleModel = new ToggleStateStore();

  readonly publishModal = new PublishModal({
    verificationStore: this,
  });

  /** Индикация запроса публикации формы */
  get publishingTable() {
    return this._basicStore.formUpdateRequest.loading;
  }

  /** Индикация запроса загрузки данных таблицы */
  get fetchingTable() {
    return this.tableStore.fetching;
  }

  /** Значение switch -> статус */
  get booleanModelToVisibility(): TableVisibilityLevel {
    if (this.visibilityToggleModel.opened) {
      return TableVisibilityLevel.supervisor;
    }

    return TableVisibilityLevel.hr;
  }

  handleSwitchChange(): void {
    this.visibilityToggleModel.toggle();
    this._debouncedFetch();
  }

  async publish(): Promise<{ isError: boolean }> {
    const isError = await this._basicStore.publishForm({
      start: this.publishModal.entryRange.toServerFormat.from,
      end: this.publishModal.entryRange.toServerFormat.to,
    });

    if (!isError) {
      this.publishModal.modalState.close();
    }

    return { isError };
  }

  async init(): Promise<void> {
    if (!this._basicStore.id) {
      return;
    }

    await this.tableStore.init({
      form_id: this._basicStore.id,
      view: TableViewMode.flat,
      visibility_level: this.booleanModelToVisibility,
      limit: 10000, // Загружаем все данные для виртуализации
    });
  }

  destroy(): void {
    this.publishModal.destroy();
    this.tableStore.destroy();
  }
}

export default VerificationFormStore;
